import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { completeOnboarding, uploadAvatar, AuthError } from "@/lib/auth";
import { useAuthUser } from "@/lib/use-auth-user";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Field } from "@/components/ui/Field";
import { Input } from "@/components/ui/Input";
import { ErrorBlock } from "@/components/ui/States";
import { MobileFrame } from "@/components/ui/MobileFrame";

const MAX_AVATAR_BYTES = 5 * 1024 * 1024; // 5MB

export function OnboardingPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuthUser();
  const queryClient = useQueryClient();
  const next = params.get("next") || "/me/meetups";

  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (user?.fullName) setName(user.fullName);
  }, [user]);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setError(null);
    if (!f) {
      setFile(null);
      return;
    }
    if (!f.type.startsWith("image/")) {
      setError("Only image files are allowed.");
      return;
    }
    if (f.size > MAX_AVATAR_BYTES) {
      setError("File is too large (max 5 MB).");
      return;
    }
    setFile(f);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Enter your name.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      let avatarUrl: string | undefined;
      if (file) {
        avatarUrl = await uploadAvatar(file);
      }
      await completeOnboarding(name.trim(), avatarUrl);
      await queryClient.invalidateQueries({ queryKey: ["auth-user-profile"] });
      navigate(next, { replace: true });
    } catch (err) {
      setError(err instanceof AuthError ? err.message : err instanceof Error ? err.message : "Couldn't save profile.");
    } finally {
      setSubmitting(false);
    }
  };

  const onSkipPhoto = async () => {
    if (!name.trim()) {
      setError("Enter your name.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await completeOnboarding(name.trim());
      await queryClient.invalidateQueries({ queryKey: ["auth-user-profile"] });
      navigate(next, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't save profile.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MobileFrame>
      <form onSubmit={onSubmit} className="flex-1 px-4 py-6 animate-fade-in" autoComplete="off">
        <h1 className="text-[24px] font-bold tracking-tight text-[#1a1a1a]">Tell us about yourself</h1>
        <p className="mt-2 text-[14px] text-[#6c757d]">
          Your name and photo will be visible to other event participants.
        </p>

        {error ? <ErrorBlock title="Error" message={error} className="mt-5" /> : null}

        <Card padding="md" className="mt-6">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="grid h-20 w-20 shrink-0 place-items-center overflow-hidden rounded-full border-2 border-dashed border-[#cbd5e1] bg-[#f8fafc] transition hover:border-[#2563eb]"
            >
              {previewUrl ? (
                <img src={previewUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="text-[11px] font-semibold text-[#64748b]">Photo</span>
              )}
            </button>
            <div className="min-w-0 flex-1">
              <p className="text-[14px] font-semibold text-[#1a1a1a]">
                {file ? file.name : "Add a photo"}
              </p>
              <p className="mt-0.5 text-[12px] text-[#6c757d]">
                JPG/PNG, up to 5 MB. You can skip — add it later.
              </p>
              {file ? (
                <button
                  type="button"
                  onClick={() => {
                    setFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  className="mt-1 text-[12px] font-semibold text-[#dc2626]"
                >
                  Remove
                </button>
              ) : null}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={onPickFile}
              className="hidden"
            />
          </div>
        </Card>

        <div className="mt-5 flex flex-col gap-4">
          <Field label="Name" required>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Alex Ivanov"
              autoComplete="name"
              required
            />
          </Field>

          <Button type="submit" loading={submitting} disabled={!name.trim()} fullWidth>
            {submitting ? "Saving..." : file ? "Save and continue" : "Save"}
          </Button>

          {file ? null : (
            <button
              type="button"
              onClick={onSkipPhoto}
              disabled={submitting || !name.trim()}
              className="text-center text-[13px] font-semibold text-[#475569] hover:text-[#2563eb] disabled:opacity-50"
            >
              Skip photo
            </button>
          )}
        </div>
      </form>
    </MobileFrame>
  );
}
